import { Product } from '@protos/product';
import { useAsync } from 'react-use';
import { useAdminApi } from './useAdminApi';

const NO_PRODUCTS: Product[] = [];

// The products a trade limit on this organization can sensibly name.
//
// Scoped to the groups this organization is a CLIENT for, because that is the only side
// a link scope applies to - `has_organization_limit` resolves metering from the client
// side. It matters more than it looks: `_move` reads the row before it consults the links,
// so an existing row gates the product whatever the links say. A row created outside the
// client-side scope is not inert, it is a live blocker sitting at allocated_lots = 0 that
// rejects every buy, and no agent can allocate against it because no link covers it.
//
// Private products are excluded (the API forces `private` on futures and future swaps)
// and so are untradable ones, which a lot limit cannot bite on.
//
// `reloadKey` lets the caller re-resolve after a link changes, since the links are owned
// by a sibling component.
export function useOrganizationProducts(organizationId: number, reloadKey: number = 0) {
  const { apiClient } = useAdminApi();

  const state = useAsync(async () => {
    if (!apiClient) return NO_PRODUCTS;

    const links = await apiClient.getOrganizationLinks({ client_organization_id: organizationId });
    const groups = [...new Set(links.filter(link => link.enabled).map(link => link.scope_product_group))];
    if (!groups.length) return NO_PRODUCTS;

    const perGroup = await Promise.all(groups.map(group => apiClient.productsLoader({ product_group: group }).loadAllData()));
    return perGroup
      .flat()
      .filter(product => !product.private && product.tradable)
      .sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [apiClient, organizationId, reloadKey]);

  return {
    products: state.value ?? NO_PRODUCTS,
    // as above: not ready is not the same as loaded and empty
    loading: state.loading || !apiClient,
    error: state.error,
  };
}
